import { useState, useEffect } from 'react';
import {Main} from 'src/Layouts';
import {Drupal, Graphql, YouInvest, EQUITY, FUND, ETF, CEF} from 'src/Api';
import {InvestWithAjBell, Disclaimer} from 'src/Components/Molecules/Card';
import {InstrumentMain, InstrumentInfoBoxes} from 'src/Components/Organisms';
import {SideNavigation} from 'src/Components/Molecules/Navigation';
import {getTitle, sendCacheHeaders} from 'src/Utils';
import moment from 'moment';

import {Equity} from './Components/InstrumentType';

export default function Summary({instrumentData}) {

    const getBreadCrumb = () => {
		if (instrumentData) {
			return `${instrumentData.quote.nameShort} (${instrumentData.quote.marketCode}) - Financials & ratios`;
		}
	}

    return <Main title={getTitle(instrumentData, 'Financials and ratios')} nav={instrumentData.nav} breadcrumb={getBreadCrumb()} marketCode={instrumentData.quote.marketCode}>
        <div className="ajb-grid-row padding-bottom-20">
            <div className="ajb-col-2 ajb-col-8-breakpoint padding-top-20 padding-bottom-0-sm">
                <SideNavigation  {...instrumentData} />
                <InvestWithAjBell />
            </div>
            <div className="ajb-col-10 ajb-col-8-breakpoint main-content-wrapper white-bg margin-top-20 padding-20 padding-10-sm">
                <InstrumentMain {...instrumentData} />
                <InstrumentInfoBoxes {...instrumentData} />              
                <Equity {...instrumentData}/>
                <Disclaimer />
            </div>
        </div>
    </Main>
}
  
export async function getServerSideProps(context) {

    const {marketCode} = context.query;
    const pageData = await YouInvest.getBasicPageData(marketCode);

    let query = `query pageData($identifier: String!, $startDate: String!, 
        $endDate: String!, 
        $statementType: StatementType!,
        $dataType: DataType!) {
        incomeStatement(identifier: $identifier, startDate: $startDate, endDate: $endDate, statementType: $statementType, dataType: $dataType) {
            currency
            revenue
            incomePreTax
            income
            dilutedEPS
            dilutedEPSNormalised
            yearEnd
        }
        keyRatios(identifier: $identifier) {
            priceEarningsRatio
            dividendYield
            priceEarningsGrowth
            returnOnEquity
            operatingMargin
            earningsPerShare
            enterpriseValue
            EBITDA
            enterpriseValueOverEBITDA
            netTangibleAssets
            priceToBookValueRatio
            priceCashFlowRatio
            priceSalesRatio
        }
        cashFlow(identifier: $identifier, startDate: $startDate, endDate: $endDate, statementType: $statementType, dataType: $dataType) {
            currency
            operatingCashFlow
            investingCashFlow
            financingCashFlow
            freeCashFlow
            yearEnd
        }
        balanceSheet(identifier: $identifier, startDate: $startDate, endDate: $endDate, statementType: $statementType, dataType: $dataType) {
            yearEnd
            statementType
            currency
            totalBorrowings
            assets {
                tangibleAssets
                intangibleAssets
                investments
                totalFixedAssets
                stocks
                debtors
                cashAndEquivalents
                otherAssets
                totalAssets
            }
            liabilities {
                creditorsBefore1Y
                creditorsAfter1Y
                otherLiabilities
                totalLiabilities
                netAssets
            }
            equity {
                stock
                sharePremium
                profitLoss
                otherEquity
                preferenceAndMinorities
                totalCapitalEmployed
            }
            ratios {
                debtToCapitalRatio
                debtToEquityRatio
                financialLeverage
                currentRatio
                quickRatio
                grossMargin
                operatingMargin
                taxRate
                salesPerEmployee
                netIncomePerEmployee
                interestCoverage
                solvencyRatio
                netMargin
                earningsPerShare
            }
            income {
                turnover
                costOfSales
                grossProfit
                operatingProfit
                pretaxProfit
            }
            cashFlow {
                cashFromOperations
                cashFlowBeforeFinancing
                increaseInCash
            }
        }
    }`;

    const statementsEndDate = moment(new Date()).format("MM/YYYY");
    const statementsStartDate = moment(moment(new Date()).subtract(72, 'months')).format("MM/YYYY");

    // Merge the data
    let instrumentData = {...pageData, ...(await Graphql.fetch(query, {
        identifier: marketCode,
        startDate: statementsStartDate,
        endDate: statementsEndDate,
        statementType: 'ANNUAL',
        dataType: 'RESTATED'
    }))};

    // Get first 4 years returned by incomeStatement, cashFlow and balanceSheet
    const incomeStatement = instrumentData.incomeStatement.slice(0, 5);
    const cashFlow = instrumentData.cashFlow.slice(0, 5);
    const balanceSheet = instrumentData.balanceSheet.slice(0, 5);

    instrumentData = {
        ...instrumentData,
        incomeStatement,
        cashFlow,
        balanceSheet
    };
    
    sendCacheHeaders(context, pageData);

    return { props: { instrumentData } }
}


